import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  IconButton,
  styled,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { BLACK, GRAY_LIGHT, GRAY_LIGHT_RGB_BORDER, GRAY_MAIN, GREEN_LOGO, GREEN_MAIN, WHITE } from '../../theme/colors';
import { useTranslationContext } from '../../hooks/translations/translationsProvider';
import settings from '../../utilities/settings';
import { useWaiter } from '../../hooks/waiter/WaiterProvider';
import { delay } from '../../utilities/delay';
import { dashboard } from '../../hooks/actions/dashboard';
import { useWeb3Context } from '@w3connector/web3provider';
import { validateAddress } from '../../helpers/ethers';
import CheckBoxTermsAndConditions from '../CheckBoxTermsAndConditions';


import { useDepositContract, useTokenContract } from '../../hooks/web3';
import { ethers } from 'ethers';
import { ETokensDepositStatus, IDepositRequestModel, IDepositStatusRequestModel } from '../../models/deposit';
import { depositRequest, depositStatusRequest } from '../../hooks/actions/deposit';
import generateRandomString from '../../helpers/randomString';
import { Trans } from 'react-i18next';
import { removeDatabaseWalletConnect } from '../../helpers/localStorage';
import { abbreviaStringa } from '../../helpers/cutAddress';

const FormCustom = styled('form')(({theme})=>({
    width:'100%',                                      
    [theme.breakpoints.up('md')]: {
        width:'80%',
    },
}))

const ResponseBox = styled(Box)(({theme})=>({
    width:'100%',
    marginTop:"30px",                                      
    [theme.breakpoints.up('md')]: {
        width:'80%',
    },
}))

interface DepositFormProps {
    setStatus: (status: boolean) => void;
    status: boolean;
};

const DepositForm: React.FC<DepositFormProps> = ({setStatus, status}) => {
    const [response, setResponse] = useState(null);
    const {  
        modalOpen,
        connectors,
        web3Error,
        web3Hooks,
        chainName,
        setModalOpen,
        setWeb3Error,
        handleDisconnect } = useWeb3Context();
    const {t} = useTranslationContext();
    const [tokenBalance, setTokenBalance] = useState<string>("");
    const [tokenBalanceRemoduled, setTokenBalanceRemoduled] = useState<string>("");
    const [maincoinBalance, setMainCoinBalance] = useState<string>("");
    const tokenContract = useTokenContract();
    const depositContract = useDepositContract();

    const [amountAvaiable, setAmountAvaible] = useState<number>(0);

    const [amount, setAmount] = useState('');
    const [errorAmount, setErrorAmount] = useState(false);
    const [errorAmountMessage, setErrorAmountMessage] = useState('')

    const [isSended, setIsSended] = useState(false);
    const [mainErrorMex, setMainErrorMex]=useState<string | null>(null)
    const [mainResponseMex, setMainResponseMex]=useState<string | null>(null)

    const {setWaiterView} = useWaiter();
    const [isChecked, setIsChecked] = useState(false); 
    const [tokenRequest, setTokenRequest]= useState<string>(generateRandomString(20));

    const [address, setAddress] = useState('');
    const [errorAddress, setErrorAddress] = useState(false);

    useEffect(()=>{
        setWaiterView(true)
        delay(500).finally(()=>{
            dashboard()
                .then(resp => {
                    setAmountAvaible(resp.balance)
                })
                .catch(error => {
                    // initLoad.current = false
                    console.log(error)
                })
            setWaiterView(false)
        })
    },[])

    useEffect(()=>{
        if(web3Hooks.isActive && web3Hooks.account){
            getTokeBalance(web3Hooks.account)
                .then(resp => {
                    setTokenBalance(resp);
                    const remoduled = (parseFloat(resp) / 101 * 100).toFixed(settings.Token.Decimals)
                    setTokenBalanceRemoduled(remoduled);
                })
                .catch(error => {
                    console.error(error)
                })
            
                getMainCoinBalance(web3Hooks.account, web3Hooks.provider)
                    .then(resp => {
                        setMainCoinBalance(resp);
                    })
                    .catch(error => {
                        console.error(error)
                    })
        }
    },[web3Hooks, amountAvaiable])

    const handleInputAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        
        setErrorAmount(false);
        setErrorAmountMessage('');
        const value = e.target.value;
        const regex = /^\d*\.?\d{0,2}$/;

        if(value === ''){
            setAmount('');
            setErrorAmount(false);
            return;
        }

        if (value.includes('.')) {
            const [integerPart, decimalPart] = value.split('.');
            if (decimalPart.length > settings.Token.Decimals) {
                setErrorAmount(false);
                return;
            }
        }

        if(!regex.test(value)){
            setErrorAmountMessage(t("Inserisci un importo valido"))
            setErrorAmount(true)
        }

        if (parseFloat(value) > 0) {
            if(parseFloat(value) > parseFloat(tokenBalanceRemoduled)){
                setAmount(parseFloat(tokenBalanceRemoduled).toFixed(settings.Token.Decimals))
                return;
            }
            setAmount(value);
            setErrorAmount(false);
            
        } 
    };

    const handleBlurAmount = () => {
        if (!errorAmount && amount) {
            const newAmount = parseFloat(amount).toFixed(settings.Token.Decimals)
            setAmount(newAmount);
            setErrorAmount(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSended(true);
        setWaiterView(true);
        setMainErrorMex(null);
        setMainResponseMex(null);

        if(!errorAmount && parseFloat(amount) > 0 && parseFloat(amount) <= parseFloat(tokenBalance) && isChecked && web3Hooks.isActive)
        {
            const newReq:IDepositRequestModel = {
                amount: parseFloat(amount),
                acceptTerms: isChecked,
                clientToken: tokenRequest
            }
            try{
                // richiesta al db
                const deposit = await depositRequest(newReq);

                const spender:string = settings.DepositContract.ContractAddress;
                const tokenParsed = ethers.utils.parseUnits(deposit.amount, 0);
                
                // approve
                const allowance: ethers.BigNumber = await tokenContract.allowance(web3Hooks.account, spender);

                if( allowance.lt(tokenParsed) )
                {
                    try{
                        const approve = await tokenContract.approve(spender, tokenParsed);
                        const approvePromise = approve.wait();
                        const timeoutPromise = new Promise((_, reject) =>
                            setTimeout(() => reject(new Error("Timeout exceeded")), 180000)
                        );
                        await Promise.race([approvePromise, timeoutPromise]);
                        await delay(30000);
                    }catch(error){
                        throw error;
                    }
                }

                try {
                    const transaction = await depositContract.DepositTokens(deposit.refCode, tokenParsed);
                    const transactionPromise = transaction.wait();
                    const timeoutPromise = new Promise<never>((_, reject) =>
                        setTimeout(() => reject(new Error("Timeout exceeded")), 180000)
                    );
                
                    // Attendere la transazione o il timeout
                    await Promise.race([transactionPromise, timeoutPromise]);
                    await delay(30000);

                    dashboard()
                        .then(resp => {
                            setAmountAvaible(resp.balance)
                        });
                    
                    setMainResponseMex(t("Transazione avvenuta con successo. Tx Hash: ") + transaction.hash)
                    setIsChecked(false);
                    setAmount('');
                    setTokenRequest(generateRandomString(20));
                } catch (error) {
                    if (error instanceof Error && error.message === "Timeout exceeded") {
                        setTokenRequest(generateRandomString(20));
                    }
                    throw error;
                }
                
            }catch(e:any){
                
                let message = t("Errore durante il deposito"); // default
    
                if (e) {
                    if (typeof e === "string") {
                        message = e;
                    } else if (e.message) {
                        message = e.message;
                    } else if (e.data?.error?.message) {
                        message = e.data.error.message;
                    } else if (e.data?.error) {
                        message = e.data.error;
                    } else if (e.data) {
                        message = e.data;
                    }
                }
                console.error("DEPOSIT", message);
                setMainErrorMex(String(message));
                setIsSended(false);
            }
            setWaiterView(false);
            setIsSended(false);
        }
        setStatus(!status); // reload delle ultime transazioni
    };

    //FUNCTIONS
    const getTokeBalance = async (account: string): Promise<string> => {
        if(web3Hooks.isActive){
            try{
                const balance = await tokenContract.balanceOf(account);
                return parseFloat(ethers.utils.formatUnits(balance, settings.Token.Decimals)).toFixed(settings.Token.Decimals);
            }catch(e){
                console.error(e);
                throw e;
            }
        }else{
            throw t("Wallet non connesso")
        }
    }

    const getMainCoinBalance = async(account: string, provider: any): Promise<string> => {
        if(web3Hooks.isActive){
            try{
                const balance = await provider.getBalance(account);
                return parseFloat(ethers.utils.formatUnits(balance, settings.Token.Blockchain.MainCoinDecimals)).toFixed(settings.Token.Blockchain.MainCoinDecimals);
            }catch(e){
                console.error(e);
                throw e;
            }
        }else{
            throw t("Wallet non connesso")
        }
    }

    return (
        <Box sx={{width:'100%', overflow: 'hidden', maxWidth: '100%', fontFamily:'poppins'}}>
            <Paper sx={{ 
                width: '100%', 
                display: 'flex', 
                minHeight:'350px', 
                border: 1, 
                borderColor: 'transparent', 
                overflow: 'hidden',
                maxWidth: '100%', // Limita la larghezza massima del Paper
                boxSizing: 'border-box'
            }}>
                
                <Box sx={{ flexGrow: 1, p: 3, position: 'relative' }}>
                    {response && (
                        <IconButton
                            onClick={() => window.print()}
                            sx={{ position: 'absolute', top: 16, right: 16 }}
                        >
                            <PrintIcon />
                        </IconButton>
                    )}

                    <Card sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
                        <CardContent 
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                //padding: '24px 0 0 0',
                                flexDirection: 'column',
                                maxWidth: '100%', // Limita il contenuto a non superare il suo contenitore
                                boxSizing: 'border-box',
                            }}
                        >
                            {/* <Typography variant="h4" gutterBottom  color={BLACK} >
                                {t("Deposita ASSO")}
                            </Typography> */}

                            { !response &&
                                <FormCustom onSubmit={handleSubmit}>
                                    <Grid container spacing={2} justifyContent={'center'} sx={{maxWidth: '100%', boxSizing: 'border-box' }}>
                                        
                                        {web3Hooks && web3Hooks.isActive ?
                                            <>
                                                {/** WALLET CONNESSO */}
                                                <Grid item xs={10} sm={8} justifyItems='center'>
                                                    {/** SEZIONE METAMASK ADDRESS -LOGO- DISCONNESSIONE */}
                                                    <Grid container spacing={1} justifyContent='center'>
                                                        <Grid item xs={12} sm='auto' padding={1} alignContent='center' marginBottom='-5px'>
                                                            <img src='/metamask.png' width='30px'/>
                                                        </Grid>
                                                        <Grid item xs={12} sm='auto' padding={1} alignContent='center'>
                                                            <Typography
                                                                sx={{fontSize:'10px'}}
                                                            >
                                                                {t("Collegato Metamask ")} {abbreviaStringa(web3Hooks.account)}
                                                            </Typography>
                                                                
                                                        </Grid>
                                                        {
                                                            web3Hooks.isActive && chainName !== settings.Token.Blockchain.ChainName &&
                                                            <Grid item xs={12} sm='auto' padding={1} alignContent='center'>
                                                                <Typography
                                                                    sx={{fontSize:'10px', color: 'red'}}
                                                                >
                                                                    {t("Attenzione! Hai selezionato una blockchain sbagliata")}
                                                                </Typography>
                                                            </Grid>
                                                        }
                                                    </Grid> 
                                                    <Button 
                                                        onClick={() => {
                                                            
                                                            removeDatabaseWalletConnect();
                                                            handleDisconnect();
                                                        }}
                                                        variant="contained"
                                                        sx={{
                                                            fontSize:"9px", 
                                                            width:'130px', 
                                                            color:WHITE, 
                                                            marginBottom:8,
                                                            backgroundColor: GREEN_LOGO,
                                                            '&:hover':{
                                                                backgroundColor:'darkgreen'
                                                            }
                                                        }}>
                                                            {t("Disconnetti Wallet")}
                                                    </Button>

                                                    {/** SEZIONE Saldo Attuale */}
                                                    <Grid container xs={12}spacing={1} paddingBottom={8}>
                                                        {
                                                            mainResponseMex && 
                                                            <Grid item xs={12} alignContent='center' paddingBottom={2}>
                                                                <Typography 
                                                                    sx={{
                                                                        fontSize:'16px',
                                                                        fontWeight:'bold'
                                                                    }}
                                                                >
                                                                    {
                                                                        t("Deposito effettuato!")
                                                                    }
                                                                </Typography>
                                                            </Grid>
                                                        }

                                                        <Grid item xs={12} alignContent='center' paddingBottom={2}>
                                                            <Typography 
                                                                sx={{
                                                                    fontSize:'12px',
                                                                    fontWeight:'bold'
                                                                }}
                                                            >
                                                                {
                                                                mainResponseMex ? 
                                                                    t("Il tuo saldo attuale ASSO è di") :
                                                                    t("Il tuo nuovo saldo ASSO è di")
                                                                }
                                                            </Typography>
                                                        </Grid>

                                                        <Grid item xs={12} sm={6} alignContent='center'>
                                                            <Box 
                                                                sx={{
                                                                    backgroundColor:`${GREEN_LOGO}20`,
                                                                    borderRadius:'5px',
                                                                    minHeight:'80px',
                                                                    alignContent:'center'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:'10px',
                                                                        padding:1
                                                                }}>
                                                                    {t("conto")}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize:'18px',
                                                                        padding:1,
                                                                        fontWeight:'bold'
                                                                }}>
                                                                    {amountAvaiable.toFixed(2)}
                                                                </Typography>
                                                                
                                                            </Box>
                                                            
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} alignContent='center'>
                                                            <Box 
                                                                    sx={{
                                                                        backgroundColor:`${GREEN_LOGO}20`,
                                                                        borderRadius:'5px',
                                                                        minHeight:'80px',
                                                                        alignContent:'center'
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize:'10px',
                                                                            padding:1
                                                                    }}>
                                                                        {t("portafogli elettronico")}
                                                                    </Typography>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize:'18px',
                                                                            padding:1,
                                                                            fontWeight:'bold'
                                                                    }}>
                                                                        {tokenBalance}
                                                                    </Typography>
                                                                    
                                                            </Box>
                                                        </Grid>

                                                        <Grid item xs={12} alignContent={'center'}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize:'10px',
                                                                    paddingTop:1
                                                            }}>
                                                                {t("Saldo spendibile nel portafogli elettronico: ")}{tokenBalanceRemoduled}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize:'10px',
                                                            }}>
                                                                {settings.Token.Blockchain.MainCoinSymbol}{t(" disponibili nel tuo wallet: ")}{parseFloat(maincoinBalance).toFixed(4)}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>

                                                    {/** SEZIONE Inserisci importo*/}
                                                    {!mainResponseMex &&
                                                        <Grid container xs={12} spacing={1} justifyContent='center'>
                                                            <Grid item xs={12} alignContent='center' paddingBottom={5}>
                                                                <Typography 
                                                                    sx={{
                                                                        fontSize:'12px',
                                                                    }}
                                                                >
                                                                    {t("Vuoi trasferire ASSO dal tuo portafogli elettronico e depositarli sul conto?")}
                                                                </Typography>
                                                                <Typography 
                                                                    sx={{
                                                                        fontSize:'12px',
                                                                    }}
                                                                >
                                                                    {t("Inserisci qui sotto la quantità di ASSO che desideri depositare")}
                                                                </Typography>
                                                            </Grid>

                                                            <Grid item xs={12} alignContent='center' paddingBottom={5}>
                                                                <TextField
                                                                    fullWidth
                                                                    label={t("Quantità ASSO")}
                                                                    name="amount"
                                                                    onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {(handleInputAmountChange(evt))}}
                                                                    onClick={() => {
                                                                        setAmount('')
                                                                        setErrorAmount(false)
                                                                    }}
                                                                    onBlur={() => handleBlurAmount()}
                                                                    value={amount}
                                                                    error={errorAmount}
                                                                    // disabled={}
                                                                    helperText={
                                                                        errorAmount ? errorAmountMessage : ""
                                                                    }
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-notchedOutline':{
                                                                            borderColor: GREEN_LOGO,
                                                                            color: GREEN_MAIN,
                                                                        },
                                                                        '& .MuiInputLabel-root':{
                                                                            top:-4,
                                                                            fontSize:'1rem',
                                                                            color: GREEN_MAIN,
                                                                        },
                                                                        '& .MuiInputLabel-root.Mui-focused':{
                                                                            color: GREEN_MAIN
                                                                        },
                                                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: GREEN_LOGO,
                                                                            color: GREEN_MAIN,
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            padding: '12px 12px',
                                                                            lineHeight: 1.4, 
                                                                            color:"grey", fontSize:'1rem',  
                                                                        },
                                                                    }}
                                                                />
                                                                
                                                            </Grid>

                                                            <Grid item xs={12} alignContent='center' paddingBottom={5}>
                                                                <CheckBoxTermsAndConditions isChecked={isChecked} setIsChecked={setIsChecked}/>
                                                            </Grid>
                                                            
                                                            <Grid item xs={6} sm={3} alignContent='center' paddingBottom={5}>
                                                                <Button type="submit" variant="contained" disabled={isSended || amount === '' || errorAmount || !isChecked} 
                                                                    sx={{
                                                                        color:WHITE, 
                                                                        border:'0',
                                                                        backgroundColor: GREEN_LOGO,
                                                                        '&:hover':{
                                                                            backgroundColor:'darkgreen'
                                                                        }
                                                                }}>
                                                                    {t("Invia")}
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    }
                                                </Grid>
                                            </>
                                            :
                                            <>
                                                {/** WALLET SCONNESSO */}
                                                <Grid item xs={7} sm='auto' justifyItems='center'>
                                                    <Grid container spacing={0}>
                                                        <Grid item xs={12} sm='auto' padding={1}>
                                                            <img src='/metamask.png' width='30px'/>
                                                        </Grid>
                                                        <Grid item xs={12} sm='auto' padding={1}>
                                                            <Typography
                                                                sx={{fontSize:'10px'}}
                                                            >
                                                                <Trans
                                                                    i18nKey="preleva-asso-scollegato-collega"
                                                                    components={{
                                                                        collega: (
                                                                        <a
                                                                            href="#"
                                                                            onClick={(e) => {
                                                                            e.preventDefault();
                                                                            removeDatabaseWalletConnect();
                                                                            if (web3Hooks && web3Hooks.isActive) {
                                                                                setAddress(web3Hooks.account!);
                                                                                setErrorAddress(false);
                                                                            } else {
                                                                                setModalOpen(true);
                                                                            }
                                                                            }}
                                                                            rel="noopener noreferrer"
                                                                        />
                                                                        ),
                                                                    }}
                                                                    />
                                                            </Typography>
                                                            <Typography
                                                                sx={{fontSize:'10px'}}
                                                            >
                                                                <Trans i18nKey='preleva-asso-scollegato-istruzioni' components={{
                                                                    istruzioni: <a href={`${settings.InfosWebsite.Url}/scarica-portafogli`} target="_blank" rel="noopener noreferrer" />,
                                                                }} />
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                    {/* <Typography color={GRAY_MAIN} textAlign={'left'} mb={2} sx={{fontSize:"0.8rem"}}>
                                                    🔴 <strong>{t("Il tuo wallet è disconnesso")}</strong>
                                                    </Typography> */}
                                                </Grid>
                                            </>
                                        }
                                        
                                        {
                                            mainErrorMex && <Grid item xs={12} display="grid" > {/** marginBottom={2} marginTop={2} */}
                                                <Typography fontSize="0.75rem" color="red" 
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3, // Limita il numero di righe del testo
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word',
                                                        maxWidth: '100%', // Impedisce l'uscita dal contenitore
                                                    }}
                                                >
                                                    {mainErrorMex}
                                                </Typography>
                                            </Grid>
                                        }

                                        {
                                            mainResponseMex && <Grid item xs={12} display="grid" > {/** marginBottom={2} marginTop={2} */}
                                                <Typography fontSize="0.75rem" color={GREEN_MAIN} 
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3, // Limita il numero di righe del testo
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word',
                                                        maxWidth: '100%', // Impedisce l'uscita dal contenitore
                                                    }}
                                                >
                                                    {mainResponseMex}
                                                </Typography>
                                            </Grid>
                                        }
                                    </Grid>
                                </FormCustom>
                            }

                            { response && 
                            <></>
                                // <ResponseBox> {response.refCode}
                                //     {
                                //         <WithdrawResponse response={response} />
                                //     }
                                // </ResponseBox> 
                            }
                        </CardContent>
                    </Card>
                </Box>
            </Paper>
        </Box>
    );
};

export default DepositForm;
